import {MouseEvent, TouchEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {components} from 'react-select';
import type {
  GroupBase,
  InputProps,
  LoadingIndicatorProps,
  MenuProps,
  OptionProps,
  PlaceholderProps,
} from 'react-select';
import {SelectOption as SelectOptionType} from '@chekin/core';
import {Loader} from '../../Loader';
import {LoaderWrapper} from './styled';

const SelectInput = <T, V, L>(
  props: InputProps<
    SelectOptionType<T, V, L>,
    false,
    GroupBase<SelectOptionType<T, V, L>>
  >,
) => {
  return (
    <components.Input {...props} autoComplete="on" className="kiosko:!text-kiosko-s" />
  );
};

const SelectPlaceholder = <T, V, L>({
  children,
  ...props
}: PlaceholderProps<
  SelectOptionType<T, V, L>,
  false,
  GroupBase<SelectOptionType<T, V, L>>
>) => {
  const {t} = useTranslation();
  const isSearchableMode =
    (props.isFocused && props.selectProps.isSearchable) ||
    props.selectProps.forcedSearchMode;
  const needToShow = !props.selectProps?.label || props.isFocused || props.hasValue;
  const transformedChildren = isSearchableMode
    ? `${t('type_to_search')}...`
    : needToShow
    ? children
    : null;

  return (
    <components.Placeholder {...props} className="kiosko:!text-kiosko-s">
      {transformedChildren}
    </components.Placeholder>
  );
};

const SelectMenu = <T, V, L>(
  props: MenuProps<
    SelectOptionType<T, V, L>,
    false,
    GroupBase<SelectOptionType<T, V, L>>
  >,
) => {
  const selectProps = props?.selectProps;

  if (!selectProps.isMenuOpen) return null;
  return (
    <components.Menu {...props} className="kiosko:!w-full kiosko:!max-h-[400px]">
      {props.children}
    </components.Menu>
  );
};

const SelectLoadingIndicator = <T, V, L>(
  props: LoadingIndicatorProps<
    SelectOptionType<T, V, L>,
    false,
    GroupBase<SelectOptionType<T, V, L>>
  >,
) => {
  const {isDisabled} = props.selectProps;

  return (
    <LoaderWrapper className="select__loading-indicator">
      <Loader height={18} width={30} color={isDisabled ? '#a5a5a5' : '#e7e7e7'} />
    </LoaderWrapper>
  );
};

const SelectOption = <T, V, L>({
  innerProps: {onClick, ...innerProps},
  ...props
}: OptionProps<
  SelectOptionType<T, V, L>,
  false,
  GroupBase<SelectOptionType<T, V, L>>
>) => {
  const handleClickAndTouch = (
    e: TouchEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>,
  ) => {
    onClick?.(e as unknown as MouseEvent<HTMLDivElement>);
  };

  return (
    <components.Option
      {...props}
      className="kiosko:!py-5 kiosko:!px-10 kiosko:!text-kiosko-s"
      innerProps={{
        ...innerProps,
        onClick: handleClickAndTouch,
        // onTouchEnd: handleClickAndTouch,
      }}
    >
      {props.children}
    </components.Option>
  );
};

export {SelectInput, SelectMenu, SelectLoadingIndicator, SelectPlaceholder, SelectOption};
